/**
 * Which per-championship stat the ranking is computed on. Stable identifiers:
 * they are persisted in `media_manager_templates.player_ranking`.
 *
 * This dimension used to be the create-time «Показатель» subtype
 * (`IndicatorSubtype`). It moved here because a template may show several stats
 * at once, so only the *ranking* needs to name one.
 */
export const PlayerRankingStat = {
  GAMES: 'games',
  GOALS: 'goals',
  ASSISTS: 'assists',
  GOALS_ASSISTS: 'goals_assists',
  YELLOW_CARDS: 'yellow_cards',
  RED_CARDS: 'red_cards',
} as const;

export type PlayerRankingStat = typeof PlayerRankingStat[keyof typeof PlayerRankingStat];

export type PlayerRankingDirection = 'desc' | 'asc';

export interface PlayerRankingSettings {
  stat: PlayerRankingStat;
  direction: PlayerRankingDirection;
  /** Max rank slots to render. Undefined = every player with a stats line. */
  limit?: number;
}

export const DEFAULT_PLAYER_RANKING: PlayerRankingSettings = {
  stat: PlayerRankingStat.GOALS,
  direction: 'desc',
};

const VALID_STAT: ReadonlySet<string> = new Set<string>(Object.values(PlayerRankingStat));
const VALID_DIRECTION: ReadonlySet<PlayerRankingDirection> = new Set<PlayerRankingDirection>([
  'desc',
  'asc',
]);

/**
 * Tolerant normalizer — same role and shape as `normalizeMatchLayout`. Takes an
 * unknown blob (a jsonb column read straight off the row) and fills the default
 * for anything missing or unrecognized, so no caller has to defend against a
 * hand-edited or older-shaped value.
 */
export function normalizePlayerRanking(raw: unknown): PlayerRankingSettings {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_PLAYER_RANKING };
  const r = raw as Record<string, unknown>;

  const stat =
    typeof r.stat === 'string' && VALID_STAT.has(r.stat)
      ? (r.stat as PlayerRankingStat)
      : DEFAULT_PLAYER_RANKING.stat;
  const direction =
    typeof r.direction === 'string' && VALID_DIRECTION.has(r.direction as PlayerRankingDirection)
      ? (r.direction as PlayerRankingDirection)
      : DEFAULT_PLAYER_RANKING.direction;

  // A limit below 1 would render nothing at all; treat it as "no limit" rather
  // than silently producing an empty image.
  const limit =
    typeof r.limit === 'number' && Number.isFinite(r.limit) && r.limit >= 1
      ? Math.floor(r.limit)
      : undefined;

  return limit === undefined ? { stat, direction } : { stat, direction, limit };
}
